<template>
    <div>

        <!-- NOT LOGGED IN -->
        <div class="container" v-if="!loggedIn">
            <span class="h1 text-danger">
                You first need to log into your account.
            </span>
        </div>


         <!-- LOGGED IN -->
        <div class="row fw-bold text-center" v-if="loggedIn">
            <div class="fw-bold h3">
                Upload Documents...
            </div>
            <div class="fw-bold">
                (JPG, JPEG, PNG & GIF allowed with max 3MB size)
            </div>

            <!-- Face Features Extraction Section -->
            <div class="verification-section" style="margin: 2px 0; padding: 8px; border: 2px solid #28a745; border-radius: 10px; background-color: #f8f9fa;">
                <div class="fw-bold h4 mb-3">🔐 Use Facial Recognition From</div>
                
                <!-- THREE BUTTONS FOR FACE EXTRACTION -->
                <div class="extract-buttons">
                    <button 
                        class="extract-btn passport-btn" 
                        @click="extractFaceFromDocument('Passport')"
                        :disabled="!getIconUrl('Passport') || isExtracting['Passport']">
                        <span v-if="isExtracting['Passport']">⏳</span>
                        <span v-else>📘</span>
                        Passport
                    </button>
                    
                    <button 
                        class="extract-btn photo-btn" 
                        @click="extractFaceFromDocument('Photo')"
                        :disabled="!getIconUrl('Photo') || isExtracting['Photo']">
                        <span v-if="isExtracting['Photo']">⏳</span>
                        <span v-else>📷</span>
                        Photo
                    </button>
                    
                    <button 
                        class="extract-btn sia-btn" 
                        @click="extractFaceFromDocument('SIABadge')"
                        :disabled="!getIconUrl('SIABadge') || isExtracting['SIABadge']">
                        <span v-if="isExtracting['SIABadge']">⏳</span>
                        <span v-else>🪪</span>
                        SIA Badge
                    </button>
                </div>
                
                <!-- Status Message -->
                <div v-if="faceStatusMessage" :class="faceStatusClass" style="padding: 8px; border-radius: 4px; margin-top: 10px;">
                    {{ faceStatusMessage }}
                </div>
                
                <div v-if="savedFeatures" class="mt-2">
                    <div class="text-success">✅ Face saved successfully for Recognition!</div>
                </div>
            </div>

            <!-- DYNAMIC TABLE - rows generated from documentsConfig -->
            <table class="container border-1">
                <thead>
                    <tr class="border-1 bg-primary">
                        <td class="border-1 text-center">Name</td>
                        <td class="border-1 text-center">Uploaded</td>
                        <td class="border-1 text-left">Action</td>
                    </tr>
                </thead>

                <tbody class="border-1">
                    <tr v-for="doc in documentsConfig" :key="doc.key" class="border-1">
                        <!-- Document Name Column -->
                        <td class="border-1">{{ doc.displayName }}</td>
                        
                        <!-- Uploaded Column (shows icon or NO) -->
                        <td class="border-1 text-center m-0 p-0">
                            <div v-if="getIconUrl(doc.key)">
                                <img :src="getIconUrl(doc.key)" 
                                     style="width:40px;height:40px;object-fit:contain;border-radius:4px;cursor:pointer;" 
                                     @click="downloadDocument(doc.key)"
                                     :title="'Click to download ' + doc.displayName" />
                            </div>
                            <span v-else class="text-danger">NO</span>
                        </td>
                        
                        <!-- Action Column (Choose File button) -->
                        <td class="border-1 text-left">
                            <div class="custom-file-input">
                                <label class="custom-file-label" @click="triggerFileInput(doc.key)">
                                    Upload {{ doc.isPdf ? 'PDF' : 'Image' }}
                                </label>
                                <input 
                                    :type="doc.isPdf ? 'file' : 'file'"
                                    :accept="doc.isPdf ? '.pdf' : 'image/*'"
                                    :ref="el => setFileInputRef(doc.key, el)"
                                    style="display: none"
                                    @change="doc.isPdf ? handlePDFFileChange(doc.key, $event) : handleFileChange(doc.key, $event)" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, ref } from "vue";

declare const faceapi: any;

export default {
    name: "UploadDocuments",
    props: ['baseUrl'],
    setup(props: any) {
        const loggedIn = ref(false);
        const documentsList = ref<string[]>([]);
        const iconUrls = ref<{ [key: string]: string }>({});
        const OfficerId = localStorage.OfficerId;
        
        const isProcessing = ref(false);
        const faceStatusMessage = ref('');
        const faceStatusClass = ref('');
        const savedFeatures = ref<number[] | null>(null);
        const faceapiLoaded = ref(false);
        
        // Track extraction loading state per document
        const isExtracting = ref<{ [key: string]: boolean }>({
            Passport: false,
            Photo: false,
            SIABadge: false
        });
        
        // Store file input references
        const fileInputs = ref<{ [key: string]: HTMLInputElement | null }>({});
        
        var token = localStorage.getItem("token")!;
        const baseUrlAddress = computed(() => `${props.baseUrl}`);

        // ============================================================
        // DOCUMENTS CONFIGURATION - Single source of truth for all rows
        // ============================================================
        const documentsConfig = [
            { key: 'Passport', displayName: 'Passport', isPdf: false, extractFace: true },
            { key: 'Photo', displayName: '35x45mm Photo', isPdf: false, extractFace: true },
            { key: 'Visa', displayName: 'Visa Share Code', isPdf: false, extractFace: false },
            { key: 'SIABadge', displayName: 'SIA Licence', isPdf: false, extractFace: true },
            { key: 'NINoDoc', displayName: 'NIN/UTR', isPdf: false, extractFace: false },
            { key: 'DrivingLicence', displayName: 'DrivingLicence', isPdf: false, extractFace: false },
            { key: 'AddressDoc', displayName: 'Proof of Address 01', isPdf: false, extractFace: false },
            { key: 'AdditionalDoc1', displayName: 'Proof of Address 02', isPdf: false, extractFace: false },
            { key: 'AdditionalDoc2', displayName: 'Supporting Document 01', isPdf: false, extractFace: false },
            { key: 'AdditionalDoc3', displayName: 'Supporting Document 02', isPdf: false, extractFace: false },
            { key: 'documents', displayName: 'All Documents PDF', isPdf: true, extractFace: false },
        ];

        const getIconUrl = (documentName: string) => {
            return iconUrls.value[documentName] || null;
        };
        
        const setFileInputRef = (docName: string, el: any) => {
            if (el) {
                fileInputs.value[docName] = el as HTMLInputElement;
            }
        };
        
        const triggerFileInput = (docName: string) => {
            if (fileInputs.value[docName]) {
                fileInputs.value[docName]?.click();
            }
        };
        
        // Get the document config by key
        const getDocumentConfig = (documentName: string) => {
            return documentsConfig.find(doc => doc.key === documentName);
        };
        
        // ============================================================
        // NEW FUNCTION: Extract face from already uploaded document
        // ============================================================
        const extractFaceFromDocument = async (documentName: string) => {
            // Check if document exists
            if (!getIconUrl(documentName)) {
                faceStatusMessage.value = `❌ No ${documentName} image found. Please upload first.`;
                faceStatusClass.value = 'text-danger';
                setTimeout(() => { faceStatusMessage.value = ''; }, 3000);
                return;
            }
            
            // Set extracting state for this document
            isExtracting.value[documentName] = true;
            faceStatusMessage.value = `📥 Downloading ${documentName}...`;
            faceStatusClass.value = 'text-info';
            
            try {
                // Download the image from server
                const extension = '.jpg';
                const downloadUrl = props.baseUrl + 'SecurityOfficers/' + OfficerId + '/download/' + documentName + extension;
                
                const response = await fetch(downloadUrl, {
                    method: 'GET',
                    headers: {
                        "Authorization": "Bearer " + localStorage.token,
                    },
                });
                
                if (!response.ok) {
                    throw new Error(`Failed to download: ${response.status}`);
                }
                
                const blob = await response.blob();
                const file = new File([blob], `${documentName}.jpg`, { type: 'image/jpeg' });
                
                faceStatusMessage.value = `🔍 Extracting face from ${documentName}...`;
                
                // Extract features from the downloaded image
                const extractedFeatures = await extractFeaturesFromLocalFile(file);
                
                if (!extractedFeatures) {
                    faceStatusMessage.value = `😞 No face detected in ${documentName}. Please upload a clearer image.`;
                    faceStatusClass.value = 'text-danger';
                    return;
                }
                
                faceStatusMessage.value = `✅ Face detected! Saving features...`;
                
                // Save features to API
                const saved = await saveFacialFeaturesToApi(extractedFeatures);
                
                if (saved) {
                    savedFeatures.value = extractedFeatures;
                    faceStatusMessage.value = `🎉 Face extracted and saved from ${documentName}!`;
                    faceStatusClass.value = 'text-success';
                } else {
                    faceStatusMessage.value = `⚠️ Face detected but failed to save.`;
                    faceStatusClass.value = 'text-warning';
                }
                
            } catch (error) {
                console.error("Extraction error:", error);
                faceStatusMessage.value = `❌ Failed to extract face from ${documentName}. Please try again.`;
                faceStatusClass.value = 'text-danger';
            } finally {
                isExtracting.value[documentName] = false;
                setTimeout(() => { faceStatusMessage.value = ''; }, 4000);
            }
        };
        
        // ============================================================
        // UPDATED: Upload ONLY - no face extraction during upload
        // ============================================================
        const handleFileChange = async (documentName: string, event: Event) => {
            const target = event.target as HTMLInputElement;
            if (!target.files || !target.files[0]) return;
            
            const file = target.files[0];
            
            // Upload ONLY — no face extraction here anymore
            const formData = new FormData();
            formData.append('profile_pic', file);
            formData.append('documentName', documentName + '.jpg');
            
            try {
                faceStatusMessage.value = `📤 Uploading ${documentName}...`;
                faceStatusClass.value = 'text-info';
                
                const response = await fetch(`${props.baseUrl}SecurityOfficers/${OfficerId}/upload`, {
                    method: 'POST',
                    headers: {
                        "Authorization": "Bearer " + localStorage.token,
                    },
                    body: formData
                });
                
                await response.json();
                
                // Refresh document list and icons
                await checkDocumentsStatus();
                await loadIcons();
                
                // Reset file input
                if (fileInputs.value[documentName]) {
                    fileInputs.value[documentName]!.value = '';
                }
                
                faceStatusMessage.value = `✅ ${documentName} uploaded successfully! ${getDocumentConfig(documentName)?.extractFace ? 'Click "Extract" button above to process face.' : ''}`;
                faceStatusClass.value = 'text-success';
                
            } catch (error) {
                console.error("Upload error:", error);
                alert("Upload failed: " + (error as Error).message);
                faceStatusMessage.value = `❌ Upload failed for ${documentName}`;
                faceStatusClass.value = 'text-danger';
            } finally {
                setTimeout(() => { faceStatusMessage.value = ''; }, 3000);
            }
        };
        
        const handlePDFFileChange = async (documentName: string, event: Event) => {
            const target = event.target as HTMLInputElement;
            if (!target.files || !target.files[0]) return;
            
            const file = target.files[0];
            
            const formData = new FormData();
            formData.append('profile_pic', file);
            formData.append('documentName', documentName + '.pdf');
            formData.append('isPdf', 'true');
            
            try {
                const response = await fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/upload', {
                    method: 'POST',
                    headers: {
                        "Authorization": "Bearer " + localStorage.token,
                    },
                    body: formData
                });
                
                await response.json();
                await checkDocumentsStatus();
                await loadIcons();
                
                // Reset file input
                if (fileInputs.value[documentName]) {
                    fileInputs.value[documentName]!.value = '';
                }
                
                faceStatusMessage.value = `✅ ${documentName} uploaded successfully!`;
                faceStatusClass.value = 'text-success';
                setTimeout(() => { faceStatusMessage.value = ''; }, 3000);
                
            } catch (error) {
                console.error("Upload error:", error);
                alert("Upload failed: " + (error as Error).message);
            }
        };
        
        const extractFeaturesFromLocalFile = (file: File): Promise<number[] | null> => {
            return new Promise((resolve) => {
                (async () => {
                    try {
                        if (!faceapiLoaded.value) {
                            await loadFaceApiModels();
                        }
                        
                        const reader = new FileReader();
                        
                        reader.onload = async (e) => {
                            const img = new Image();
                            
                            img.onload = async () => {
                                try {
                                    let detections: any = null;
                                    
                                    try {
                                        detections = await faceapi
                                            .detectAllFaces(img)
                                            .withFaceLandmarks()
                                            .withFaceDescriptors();
                                    } catch (err) {
                                        const options = new faceapi.TinyFaceDetectorOptions({
                                            inputSize: 320,
                                            scoreThreshold: 0.5
                                        });
                                        detections = await faceapi
                                            .detectAllFaces(img, options)
                                            .withFaceLandmarks()
                                            .withFaceDescriptors();
                                    }
                                    
                                    if (!detections || detections.length === 0) {
                                        resolve(null);
                                        return;
                                    }
                                    
                                    if (detections.length > 1) {
                                        faceStatusMessage.value = '⚠️ Multiple faces detected. Use single face image.';
                                        resolve(null);
                                        return;
                                    }
                                    
                                    const descriptor = detections[0].descriptor;
                                    const features: number[] = Array.isArray(descriptor) ? 
                                        descriptor.map((value: any) => Number(value)) : 
                                        Object.values(descriptor).map((value: any) => Number(value));
                                    
                                    resolve(features);
                                    
                                } catch (error) {
                                    console.error('Face detection error:', error);
                                    resolve(null);
                                }
                            };
                            
                            img.onerror = () => resolve(null);
                            img.src = e.target?.result as string;
                        };
                        
                        reader.onerror = () => resolve(null);
                        reader.readAsDataURL(file);
                        
                    } catch (error) {
                        console.error('Error:', error);
                        resolve(null);
                    }
                })();
            });
        };
        
        const loadIcons = async () => {
            if (!OfficerId) {
                console.error("No OfficerId found");
                return;
            }
            
            try {
                const response = await fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/icons', {
                    method: 'GET',
                    headers: {
                        "Authorization": "Bearer " + localStorage.token,
                    },
                });
                
                const data = await response.json();
                const urlMap: { [key: string]: string } = {};
                
                if (Array.isArray(data)) {
                    data.forEach((url: string) => {
                        const match = url.match(/\/([^/?]+)\.(jpg|jpeg|png|gif|ico)/i);
                        if (match) {
                            const fileName = match[1];
                            urlMap[fileName] = url;
                        }
                    });
                }
                
                iconUrls.value = urlMap;
            } catch (err) {
                console.error(err);
            }
        };
        
        const checkDocumentsStatus = async () => {
            if (!OfficerId) return;
            
            try {
                const response = await fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/documents', {
                    method: 'GET',
                    headers: {
                        "Authorization": "Bearer " + localStorage.token,
                    },
                });
                const data = await response.json();
                documentsList.value = Array.isArray(data) ? data : [];
            } catch (error) {
                console.error(error);
            }
        };
        
        const downloadDocument = (fileName: string) => {
            let extension = '';
            let downloadUrl = '';
            
            if (fileName === 'documents') {
                extension = '.pdf';
                downloadUrl = props.baseUrl + 'SecurityOfficers/' + OfficerId + '/download/' + fileName + extension;
            } else {
                extension = '.jpg';
                downloadUrl = props.baseUrl + 'SecurityOfficers/' + OfficerId + '/download/' + fileName + extension;
            }
            
            fetch(downloadUrl, {
                method: 'GET',
                headers: {
                    "Authorization": "Bearer " + localStorage.token,
                },
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Download failed: ${response.status}`);
                }
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName + extension;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(error => {
                console.error("Download error:", error);
                alert("Failed to download file. Please try again.");
            });
        };
        
        const loadFaceApiModels = async () => {
            try {
                if (typeof faceapi === 'undefined') {
                    faceStatusMessage.value = 'Face detection library not loaded. Please refresh the page.';
                    faceStatusClass.value = 'text-danger';
                    console.error('face-api.js not loaded');
                    return false;
                }
                
                const MODEL_URL = '/models';
                
                await Promise.all([
                    faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
                    faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
                    faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
                    faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
                ]);
                
                faceapiLoaded.value = true;
                console.log('Face-api models loaded');
                return true;
            } catch (error) {
                console.error('Failed to load face-api models:', error);
                faceStatusMessage.value = 'Failed to load face detection models';
                faceStatusClass.value = 'text-danger';
                return false;
            }
        };
        
        const saveFacialFeaturesToApi = async (features: number[]): Promise<boolean> => {
            try {
                const response = await fetch(`${props.baseUrl}SecurityOfficers/${OfficerId}/SaveFacialFeature`, {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({ 
                        FacialFeatures: features
                    })
                });
                
                return response.ok;
            } catch (error) {
                console.error('API Error:', error);
                return false;
            }
        };
        
        //const token = localStorage.getItem("token");
        if (token == null) {
            loggedIn.value = false;
        } else {
            loggedIn.value = true;
            //checkDocumentsStatus();
            loadIcons();
        }
        
        return {
            loggedIn,
            documentsList,
            documentsConfig,
            getIconUrl,
            downloadDocument,
            isProcessing,
            faceStatusMessage,
            faceStatusClass,
            savedFeatures,
            isExtracting,
            setFileInputRef,
            triggerFileInput,
            handleFileChange,
            handlePDFFileChange,
            extractFaceFromDocument,  // <-- EXPOSED for buttons
        };
    }
};
</script>

<style scoped>
.border-1 {
    border: 1px solid #dee2e6;
}

.bg-primary {
    background-color: #007bff;
    color: white;
}

.text-center {
    text-align: center;
}

.text-left {
    text-align: left;
}

.text-danger {
    color: #dc3545;
    font-weight: bold;
}

.text-success {
    color: #28a745;
    font-weight: bold;
}

.text-warning {
    color: #ffc107;
    font-weight: bold;
}

.text-info {
    color: #17a2b8;
    font-weight: bold;
}

/* Main wrapper - centers ALL content */
.row.fw-bold.text-center {
    max-width: 400px;
    margin: 0 auto;
    padding: 0 !important;
    display: block;
}

/* Container style for consistency */
.container {
    max-width: 400px;
    margin: 0 auto;
    padding: 0 !important;
}

/* Top text sections */
.fw-bold.h3,
.fw-bold:not(.h3):not(.h4) {
    text-align: center;
    width: 100%;
    word-wrap: break-word;
}

/* Face verification section */
.verification-section {
    max-width: 100%;
    margin: 2px 0 !important;
    padding: 8px !important;
    box-sizing: border-box;
}

.verification-section .fw-bold.h4 {
    margin: 0 0 8px 0;
    text-align: center;
}

/* Extract Buttons Container */
.extract-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    margin-bottom: 12px;
}

/* Extract Button Styles */
.extract-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    cursor: pointer;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    transition: all 0.2s;
    white-space: nowrap;
}

.extract-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.passport-btn {
    background-color: #007bff;
    color: white;
}

.passport-btn:hover:not(:disabled) {
    background-color: #0056b3;
}

.photo-btn {
    background-color: #17a2b8;
    color: white;
}

.photo-btn:hover:not(:disabled) {
    background-color: #0f6674;
}

.sia-btn {
    background-color: #fd7e14;
    color: white;
}

.sia-btn:hover:not(:disabled) {
    background-color: #e66a00;
}

/* Status message */
.verification-section div[v-if="faceStatusMessage"] {
    margin-top: 8px !important;
    padding: 6px !important;
    font-size: 12px;
}

/* Table layout */
table {
    border-collapse: collapse;
    width: 100%;
    table-layout: auto;
}

td, th {
    padding: 8px 4px !important;
    margin: 0 !important;
}

td:first-child, th:first-child {
    width: 1%;
    white-space: nowrap;
    padding-right: 8px !important;
}

td:nth-child(2), th:nth-child(2) {
    width: 1%;
    white-space: nowrap;
    text-align: center;
}

td:nth-child(3), th:nth-child(3) {
    width: auto;
    text-align: left;
}

thead td {
    padding: 8px 4px !important;
}

.custom-file-input {
    position: relative;
    display: inline-block;
}

.custom-file-label {
    display: inline-block;
    padding: 6px 10px;
    cursor: pointer;
    background-color: #007bff;
    color: white;
    border-radius: 4px;
    font-size: 13px;
    transition: background-color 0.2s;
    white-space: nowrap;
}

.custom-file-label:hover {
    background-color: #0056b3;
}

/* Mobile */
@media (max-width: 640px) {
    td, th {
        padding: 4px 2px !important;
        font-size: 11px;
    }
    
    td:first-child, th:first-child {
        padding-right: 4px !important;
    }
    
    .custom-file-label {
        padding: 4px 6px;
        font-size: 10px;
    }
    
    .extract-btn {
        padding: 5px 8px;
        font-size: 10px;
    }
    
    .fw-bold.h3 {
        font-size: 1.1rem;
    }
    
    .fw-bold {
        font-size: 0.8rem;
    }
    
    .verification-section .fw-bold.h4 {
        font-size: 0.9rem;
    }
}
</style>